/**
 * InputErrors Component
 * Responsible for showing input level errors specific to a particular field
 *
 * Params:
 * input: The field that the errors will show for
 */
import {
  Component,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  Host,
  Optional,
  HostBinding,
  Inject
} from '@angular/core';
import { NgClass } from '@angular/common';
import { ViewportRuler } from '@angular/cdk/scrolling';

import { InputStatus } from '../input-status/input-status.enum';
import { FormComponent } from '../form.component';
import { InputGroupComponent } from '../input-group/input-group.component';
import { TranslatePipe } from '../../../pipes/translate.pipe';

import type { OnInit, OnDestroy } from '@angular/core';
import type { NgModel, ValidationErrors } from '@angular/forms';
import type { Subscription } from 'rxjs';

import type { InputValidationError } from '../domain/form.domain';
import type { InterpolateParams } from '../../../pipes/translate.pipe';

@Component({
  selector: 'td-wb-input-errors',
  templateUrl: 'input-errors.component.html',
  styleUrls: ['input-errors.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, TranslatePipe]
})
export class InputErrorsComponent implements OnInit, OnDestroy {
  /**
   * @param errorKeyNamespace
   * @description A dot-separated string that maps to a namespace entry on the
   * corresponding language's locale file
   * @example <td-wb-input-errors errorKeyNamespace='tdWb.form.age'></td-wb-input-errors>
   */
  @Input() errorKeyNamespace: string = undefined;
  /**
   * @param errorKeyNamespaceParams
   * @description An object containing a value that is to be interpolated by the tdWbTranslate params
   * @example <td-wb-input-errors errorKeyNamespace='tdWb.form.age' [errorKeyNamespaceParams]='{min: 18, max: 65s}'></td-wb-input-errors>
   * Locale string value is "max": "Age should be below {{max}} years old."
   * Final result is "Age should be below 65 years old"
   */
  @Input() errorKeyNamespaceParams: InterpolateParams = {};
  @Input() errorKeyDisplayBeforeSubmit: Array<string>;
  @Input() displayErrorForPristineState: boolean;
  @Input() align: 'left' | 'right' = 'left';
  @Input() multiLine: boolean;
  @Input() multiLineSpaceReserve: boolean = true;
  @Input() hasInputNote: boolean = false;

  @HostBinding('class.td-wb-input-errors--multi-line')
  get isMultiLine(): boolean {
    return this.multiLine;
  }

  @HostBinding('class.td-wb-input-errors--multi-line--spaced')
  get isMultiLineSpaceReserved(): boolean {
    return this.multiLine && this.multiLineSpaceReserve;
  }

  private inputControl: NgModel;
  private statusChangesSubscription: Subscription;
  private displayBeforeSubmitErrorKeys: Record<string, unknown> = {};
  private errors: Array<InputValidationError> = [];
  private forceDisplay: boolean = false;

  constructor(@Inject(FormComponent) private form: FormComponent,
              @Inject(InputGroupComponent) @Host() @Optional() private inputGroup: InputGroupComponent,
              @Inject(ElementRef) private elementRef: ElementRef,
              @Inject(ViewportRuler) private viewportRuler: ViewportRuler,
              @Inject(ChangeDetectorRef) private changeDetectorRef: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    if (this.errorKeyDisplayBeforeSubmit instanceof Array) {
      this.errorKeyDisplayBeforeSubmit.forEach((errorKey: string): void => {
        this.displayBeforeSubmitErrorKeys[errorKey] = true;
      });
    }
  }

  ngOnDestroy(): void {
    if (this.statusChangesSubscription !== undefined) {
      this.statusChangesSubscription.unsubscribe();
      this.form.setAccessibleError(this.inputControl.name, undefined);
    }
  }

  subscribeToStatusChange(inputControl: NgModel): void {
    if (inputControl !== undefined && inputControl !== null) {
      this.inputControl = inputControl;
      if (this.statusChangesSubscription === undefined) {
        this.statusChangesSubscription = this.inputControl.statusChanges
          .subscribe((status: string): void => {
            this.changeDetectorRef.markForCheck();
            this.handleStatusChanges(status === InputStatus.INVALID);
          });
      }
    }
  }

  shouldForceDisplay(): boolean {
    if (!this.displayErrorForPristineState && this.isControlPristine()) {
      return false;
    }
    return this.forceDisplay;
  }

  handleStatusChanges(isInvalid: boolean): void {
    const inputErrors = this.inputControl === undefined ? {} : this.inputControl.errors;
    this.onInputErrorsChange(inputErrors);

    if (this.form !== undefined && this.inputControl !== undefined) {
      this.errors.forEach((error: InputValidationError): void => {
        error.isDisplayed = isInvalid && (this.shouldForceDisplay() || this.isSubmitted());
      });
      this.form.setAccessibleError(this.inputControl.name, this.errors.length > 0 ? this.errors[0] : undefined);
    }
  }

  isErrorDisplayed(): boolean {
    // display error only for submitted values and forced display errors
    // do not display errors when submitted value were reset to undefined or null
    const hasValue = (this.inputControl?.value ?? null) !== null;
    const isFormSubmitted = this.isSubmitted();
    const hasErrors = this.errors.length > 0;
    const shouldDisplayForcefully = this.shouldForceDisplay();

    return (hasValue && isFormSubmitted && hasErrors) || shouldDisplayForcefully;
  }

  isSubmitted(): boolean {
    if (this.inputControl !== undefined) {
      // eslint-disable-next-line no-prototype-builtins
      return this.form.isFormSubmitted() && this.inputControl.control.hasOwnProperty('submitted');
    }
    return false;
  }

  onInputErrorsChange(inputErrors: ValidationErrors): void {
    this.errors = [];
    let errorToDisplay: InputValidationError;

    if (inputErrors !== undefined && inputErrors !== null) {
      this.forceDisplay = this.form !== undefined && this.isSubmitted();
      errorToDisplay = this.getErrorToDisplay(inputErrors);
      if (errorToDisplay !== undefined) {
        this.errors.push(errorToDisplay);
      }
    }
  }

  getErrorToDisplay(inputErrors: ValidationErrors): InputValidationError {
    let errorToDisplay: InputValidationError;
    let i = 0;
    if (inputErrors !== undefined && inputErrors !== null) {
      Object.keys(inputErrors).forEach((key: string): void => {
        if (i === 0 || this.displayBeforeSubmitErrorKeys[key]) {
          this.forceDisplay = this.forceDisplay || this.isDisplayBeforeSubmit(key);
          errorToDisplay = this.getError(key);
        }
        i++;
      });
    }
    return errorToDisplay;
  }

  getErrors(): Array<InputValidationError> {
    return this.errors;
  }

  singleLineError(): boolean {
    return !this.multiLine;
  }

  scrollIntoView(): void {
    if (this.elementRef.nativeElement !== null) {
      this.elementRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }
    this.focusInput();
  }

  focusInput(): void {
    if ((this.inputGroup ?? null) !== null) {
      this.inputGroup.focusInput();
    }
  }

  private getError(key: string): InputValidationError {
    return {
      key,
      messageKey: `${this.errorKeyNamespace}.${key}`,
      isDisplayed: this.isDisplayBeforeSubmit(key) || this.isErrorDisplayed(),
      scrollIntoView: (): void => this.scrollIntoView(),
      isInViewPort: (): boolean => this.isVisible(),
      focusInput: (): void => this.focusInput()
    };
  }

  private isVisible(): boolean {
    if (this.elementRef.nativeElement !== null) {
      const rect = this.elementRef.nativeElement.getBoundingClientRect();
      const viewPortRect = this.viewportRuler.getViewportRect();
      return rect.top >= 0 && rect.left >= 0 && rect.bottom < viewPortRect.height && rect.right < viewPortRect.width;
    }
    return true;
  }

  private isDisplayBeforeSubmit(key: string): boolean {
    return this.displayBeforeSubmitErrorKeys[key] !== undefined && this.displayBeforeSubmitErrorKeys[key] !== null;
  }

  private isControlPristine(): boolean {
    return this.inputControl !== undefined && this.inputControl.control.pristine && this.inputControl.control.untouched;
  }
}
